import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useGetList, useGetIdentity } from 'react-admin';
import {
  ConversationFilter,
} from '../types/tickets.types';
import type {
  Conversation,
  ConversationListOptions,
  FilterState,
  UnreadCountMap,
  TypingUsersMap,
  UseConversationListReturn,
} from '../types/tickets.types';
import { useConversationListSubscription } from '../../../websocket/hooks';

const PER_PAGE = 50;

/**
 * Build React Admin filter object from ConversationListOptions
 */
const buildApiFilter = (
  options: ConversationListOptions,
  filter: ConversationFilter,
  currentUserId?: number
): Record<string, unknown> => {
  const apiFilter: Record<string, unknown> = {};

  switch (filter) {
    case ConversationFilter.MINE:
      if (currentUserId) {
        apiFilter.assignedUserId_eq = currentUserId;
      }
      break;
    case ConversationFilter.ALL:
    default:
      break;
  }

  if (options.assignment === 'unassigned') {
    apiFilter.assignedUserId_null = true;
  } else if (options.assignment === 'mine' && currentUserId) {
    if (filter !== ConversationFilter.MINE) {
      apiFilter.assignedUserId_eq = currentUserId;
    }
  }

  if (options.search) {
    apiFilter.lastMessage_q = options.search;
  }

  if (options.status) {
    apiFilter.ticketStatus_eq = options.status;
  }

  if (options.priority) {
    apiFilter.ticketPriority_eq = options.priority;
  }

  if (options.queueUuid) {
    apiFilter.ticketQueueUuid_eq = options.queueUuid;
  }

  if (options.channelUuid) {
    apiFilter.channelConnectionUuid_eq = options.channelUuid;
  }

  if (options.assignedUserId !== undefined && options.assignedUserId !== null && !options.assignment) {
    apiFilter.assignedUserId_eq = options.assignedUserId;
  }

  if (options.unreadOnly) {
    apiFilter.unread_eq = true;
  }

  return apiFilter;
};

const calculateUnreadCounts = (conversations: Conversation[]): UnreadCountMap => {
  return conversations.reduce((acc, conv) => {
    acc[Number(conv.id)] = conv.unreadCount || 0;
    return acc;
  }, {} as UnreadCountMap);
};

/**
 * useConversationList Hook
 *
 * Manages conversation list data with comprehensive filtering, sorting,
 * infinite scroll pagination, and real-time updates.
 */
export function useConversationList(): UseConversationListReturn {
  const { identity } = useGetIdentity();
  const currentUserId = identity?.id as number | undefined;

  // Filter state
  const [activeFilter, setActiveFilter] = useState<ConversationFilter>(
    ConversationFilter.ALL
  );
  const [filterOptions, setFilterOptionsState] = useState<ConversationListOptions>({
    filter: ConversationFilter.ALL,
    page: 1,
    perPage: PER_PAGE,
    sortBy: 'lastActivityAt',
    sortOrder: 'desc',
  });

  // Infinite scroll state
  const [pageNumber, setPageNumber] = useState(1);
  const [accumulatedConversations, setAccumulatedConversations] = useState<Conversation[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const prevFilterKey = useRef('');

  // Build a key that changes when any filter changes (to reset pagination)
  const filterKey = useMemo(
    () => JSON.stringify({
      activeFilter,
      search: filterOptions.search,
      status: filterOptions.status,
      priority: filterOptions.priority,
      queueUuid: filterOptions.queueUuid,
      channelUuid: filterOptions.channelUuid,
      assignment: filterOptions.assignment,
      sortBy: filterOptions.sortBy,
      sortOrder: filterOptions.sortOrder,
    }),
    [activeFilter, filterOptions.search, filterOptions.status, filterOptions.priority,
     filterOptions.queueUuid, filterOptions.channelUuid, filterOptions.assignment,
     filterOptions.sortBy, filterOptions.sortOrder]
  );

  // Reset accumulated list when filters change
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      setAccumulatedConversations([]);
      setPageNumber(1);
      setHasMore(true);
    }
  }, [filterKey]);

  const apiFilter = useMemo(
    () => buildApiFilter(filterOptions, activeFilter, currentUserId),
    [filterOptions, activeFilter, currentUserId]
  );

  const {
    data: fetchedConversations = [],
    total = 0,
    isLoading,
    error,
    refetch,
  } = useGetList<Conversation>('conversation', {
    pagination: {
      page: pageNumber,
      perPage: PER_PAGE,
    },
    sort: {
      field: filterOptions.sortBy || 'lastActivityAt',
      order: filterOptions.sortOrder?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
    },
    filter: apiFilter,
  });

  // Accumulate conversations for infinite scroll
  useEffect(() => {
    if (fetchedConversations.length > 0) {
      if (pageNumber === 1) {
        setAccumulatedConversations(fetchedConversations);
      } else {
        setAccumulatedConversations((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const newItems = fetchedConversations.filter((c) => !existingIds.has(c.id));
          return [...prev, ...newItems];
        });
      }
      setHasMore(fetchedConversations.length === PER_PAGE);
    } else if (pageNumber === 1) {
      setAccumulatedConversations([]);
      setHasMore(false);
    } else {
      setHasMore(false);
    }
  }, [fetchedConversations, pageNumber]);

  const unreadCountMap = useMemo(
    () => calculateUnreadCounts(accumulatedConversations),
    [accumulatedConversations]
  );

  const typingUsersMap: TypingUsersMap = useMemo(() => ({}), []);

  const setFilterOptions = useCallback(
    (options: Partial<ConversationListOptions>) => {
      setFilterOptionsState((prev) => ({
        ...prev,
        ...options,
        page: options.page !== undefined ? options.page : 1,
      }));
    },
    []
  );

  const handleSetActiveFilter = useCallback(
    (filter: ConversationFilter) => {
      setActiveFilter(filter);
      setFilterOptionsState((prev) => ({
        ...prev,
        filter,
        page: 1,
      }));
    },
    []
  );

  const setSearchQuery = useCallback(
    (query: string) => {
      setFilterOptions({ search: query });
    },
    [setFilterOptions]
  );

  const setSortBy = useCallback(
    (sortBy: 'lastActivityAt' | 'createdAt') => {
      setFilterOptionsState((prev) => ({
        ...prev,
        sortBy,
        page: 1,
      }));
    },
    []
  );

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      setPageNumber((prev) => prev + 1);
    }
  }, [hasMore, isLoading]);

  const refresh = useCallback(() => {
    setAccumulatedConversations([]);
    setPageNumber(1);
    setHasMore(true);
    refetch();
  }, [refetch]);

  const markAsRead = useCallback(async (conversationId: number) => {
    console.log('Mark as read:', conversationId);
  }, []);

  const handleConversationCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  useConversationListSubscription({
    onConversationCreated: handleConversationCreated,
  });

  const filterState: FilterState = {
    activeFilter,
    options: {
      ...filterOptions,
      filter: activeFilter,
    },
    isExpanded: false,
  };

  return {
    conversations: accumulatedConversations,
    totalCount: total,
    filterState,
    unreadCountMap,
    typingUsersMap,
    isLoading,
    hasMore,
    error: error as Error | null,
    setFilterOptions,
    setActiveFilter: handleSetActiveFilter,
    setSearchQuery,
    setSortBy,
    loadMore,
    refresh,
    markAsRead,
  };
}
